import React from "react";
import {
  Table,
  Header,
  TableProps,
  Popover,
  Button,
  KeyValuePairs,
  KeyValuePairsProps,
} from "@amzn/awsui-components-react";
import { FixedFeeConfig } from "@amzn/fuse-accounting-central-datastore-client";
import { NumericValue } from "@smithy/core/serde";
import {
  getAcidLink,
  getFlashLink,
  getFusenWfLink,
  getTransactionWfLink,
} from "src/commons/external-link-helper";
import { Domain } from "src/config/BartLambdaConfig";
import Decimal from "decimal.js";

interface Props {
  fixedFeeConfig: FixedFeeConfig;
  domain: Domain;
}

const DUMMY_COLUMN_DEFINITION = {
  id: "",
  header: "",
  cell: () => "",
};

// Helper function to format numeric values
const formatNumber = (value: any) => {
  if (!value) return "-";
  return Number(
    value instanceof NumericValue ? value.string : value.toString(),
  ).toLocaleString("en-US", {
    maximumFractionDigits: 4,
  });
};

export const AdjustmentsTable: React.FC<Props> = ({
  fixedFeeConfig,
  domain,
}) => {
  if (
    !fixedFeeConfig.adjustments ||
    Object.keys(fixedFeeConfig.adjustments).length === 0
  ) {
    return null;
  }

  // Helper to format colored amounts
  const formatColoredAmount = (value: any, label?: string) => {
    if (!value) return "-";
    const numValue =
      typeof value === "object" && value.string
        ? Number(value.string)
        : Number(value);

    if (numValue === 0) return "0";

    const color = numValue > 0 ? "#00802f" : "#db0000";
    const displayValue = Math.abs(numValue).toLocaleString("en-US");

    return (
      <span style={{ color }}>
        {displayValue}
        {label && ` (${label})`}
      </span>
    );
  };

  // Helper to get transaction items for KeyValuePairs
  const getTransactionItems = (
    externalId: string,
    uslIdempotenceIdPrefix: string,
  ) => {
    const isTest = fixedFeeConfig.environment === "TEST";
    return [
      {
        label: "Transaction(s)",
        value: (
          <a
            href={`/transactionsearch/external_id/${externalId}`}
            target="_blank"
          >
            {externalId}
          </a>
        ),
      },
      {
        label: "FuseBillingService Workflows",
        value: (
          <span>
            <a href={getTransactionWfLink(externalId, domain)} target="_blank">
              Transaction WF
            </a>
            ,&nbsp;
            <a href={getFusenWfLink(externalId, domain)} target="_blank">
              FUSEN WF
            </a>
          </span>
        ),
      },
      {
        label: "Other Links",
        value: (
          <span>
            <a href={getAcidLink(externalId, domain)} target="_blank">
              ACID
            </a>
            ,&nbsp;
            <a
              href={getFlashLink(
                `${uslIdempotenceIdPrefix}.${externalId.substring(externalId.lastIndexOf(".") + 1)}.${externalId}`,
                domain,
                isTest,
              )}
              target="_blank"
            >
              FLASH
            </a>
          </span>
        ),
      },
    ];
  };

  // Helper to get transaction links for popover
  const getTransactionPopover = (item: any, executionIndex: number) => {
    if (
      !item.adjustmentAmount &&
      !item.receivableShiftAmount &&
      !item.deferralRevenueShiftAmount
    ) {
      return <span>-</span>;
    }

    const adjustmentAmount = item.adjustmentAmount
      ? typeof item.adjustmentAmount === "object" &&
        item.adjustmentAmount.string
        ? Number(item.adjustmentAmount.string)
        : Number(item.adjustmentAmount)
      : 0;

    const receivableShiftAmount = item.receivableShiftAmount
      ? typeof item.receivableShiftAmount === "object" &&
        item.receivableShiftAmount.string
        ? Number(item.receivableShiftAmount.string)
        : Number(item.receivableShiftAmount)
      : 0;

    const deferralRevenueShiftAmount = item.deferralRevenueShiftAmount
      ? typeof item.deferralRevenueShiftAmount === "object" &&
        item.deferralRevenueShiftAmount.string
        ? Number(item.deferralRevenueShiftAmount.string)
        : Number(item.deferralRevenueShiftAmount)
      : 0;

    const groups: KeyValuePairsProps.Group[] = [];

    // Adjustment transaction
    if (adjustmentAmount !== 0) {
      const isTrueUp = adjustmentAmount > 0;
      const adjustmentExternalId = `${fixedFeeConfig.fixedFeeId}-${executionIndex}.${isTrueUp ? "TrueUp" : "TrueDown"}`;

      groups.push({
        type: "group",
        title: "Adjustment Transaction",
        items: getTransactionItems(
          adjustmentExternalId,
          "Axon.Fuse.Transaction",
        ),
      });
    }

    // SubBalanceShift transaction
    if (receivableShiftAmount !== 0 || deferralRevenueShiftAmount !== 0) {
      const subBalanceShiftExternalId = `${fixedFeeConfig.fixedFeeId}-${executionIndex}.SubBalShift`;

      groups.push({
        type: "group",
        title: "SubBalanceShift Transaction",
        items: getTransactionItems(
          subBalanceShiftExternalId,
          "Axon.Fuse.Charge",
        ),
      });
    }

    return (
      <Popover
        size="large"
        position="top"
        triggerType="custom"
        dismissButton={false}
        content={<KeyValuePairs columns={groups.length} items={groups} />}
      >
        <Button>Fetch</Button>
      </Popover>
    );
  };

  const adjustmentColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    DUMMY_COLUMN_DEFINITION,
    {
      id: "transaction",
      header: "Transaction",
      cell: (item: any) =>
        item.executionIndex !== undefined ? (
          getTransactionPopover(item, item.executionIndex)
        ) : (
          <></>
        ),
    },
    {
      id: "adjustmentDate",
      header: "Adjustment Date",
      cell: (item: any) => item.adjustmentDate,
      sortingField: "adjustmentDate",
    },
    {
      id: "periodStartDate",
      header: "Period Start Date",
      cell: (item: any) => item.periodStartDate || "-",
      sortingField: "periodStartDate",
    },
    {
      id: "periodEndDate",
      header: "Period End Date",
      cell: (item: any) => item.periodEndDate || "-",
      sortingField: "periodEndDate",
    },
    {
      id: "activeBenefitCount",
      header: "Active Benefit Count",
      cell: (item: any) =>
        item.activeBenefitCount !== undefined
          ? item.activeBenefitCount.toLocaleString("en-US")
          : "-",
      sortingField: "activeBenefitCount",
    },
    {
      id: "deferredRevenue",
      header: "Deferred Revenue",
      cell: (item: any) => formatNumber(item.deferredRevenue),
      sortingField: "deferredRevenue",
    },
    {
      id: "revisedPlanPrice",
      header: "Revised Plan Price",
      cell: (item: any) => formatNumber(item.revisedPlanPrice),
      sortingField: "revisedPlanPrice",
    },
    {
      id: "adjustmentAmount",
      header: "Adjustment Amount",
      cell: (item: any) => {
        if (!item.adjustmentAmount) return "-";
        const numValue =
          typeof item.adjustmentAmount === "object" &&
          item.adjustmentAmount.string
            ? Number(item.adjustmentAmount.string)
            : Number(item.adjustmentAmount);
        if (numValue === 0) return "0";
        const label = numValue > 0 ? "True-Up" : "True-Down";
        return formatColoredAmount(item.adjustmentAmount, label);
      },
      sortingField: "adjustmentAmount",
    },
  ];

  if (fixedFeeConfig.reclassBalance) {
    adjustmentColumnDefinitions.push(
      {
        id: "receivableShiftAmount",
        header: "Receivable Shift Amount",
        cell: (item: any) => formatColoredAmount(item.receivableShiftAmount),
        sortingField: "receivableShiftAmount",
      },
      {
        id: "deferralRevenueShiftAmount",
        header: "Deferral Revenue Shift Amount",
        cell: (item: any) =>
          formatColoredAmount(item.deferralRevenueShiftAmount),
        sortingField: "deferralRevenueShiftAmount",
      },
      {
        id: "longTermReceivables",
        header: "Long-term Receivables",
        cell: (item: any) => formatNumber(item.longTermReceivables),
        sortingField: "longTermReceivables",
      },
      {
        id: "longTermDeferredRevenue",
        header: "Long-term Deferred Revenue",
        cell: (item: any) => formatNumber(item.longTermDeferredRevenue),
        sortingField: "longTermDeferredRevenue",
      },
      {
        id: "unbilledAccountsReceivables",
        header: "Unbilled AR",
        cell: (item: any) => formatNumber(item.unbilledAccountsReceivables),
        sortingField: "unbilledAccountsReceivables",
      },
      {
        id: "unearnedRevenue",
        header: "Unearned Revenue",
        cell: (item: any) => formatNumber(item.unearnedRevenue),
        sortingField: "unearnedRevenue",
      },
    );
  }

  adjustmentColumnDefinitions.push(DUMMY_COLUMN_DEFINITION);

  // Get executed adjustments with their index
  const executedAdjustmentDates = Object.entries(fixedFeeConfig.adjustments)
    .filter(([_, adjustment]) => adjustment.adjustmentAmount !== undefined)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date], index) => ({ date, index: index + 1 }));

  // If reclass is enabled, calculate the LT/ST amount as iterated
  const adjustmentData = [];

  let longTermReceivables: Decimal | undefined,
    longTermDeferredRevenue: Decimal | undefined,
    unbilledAccountsReceivables: Decimal | undefined,
    unearnedRevenue: Decimal | undefined;

  if (fixedFeeConfig.reclassBalance) {
    longTermReceivables = new Decimal(
      fixedFeeConfig.reclassBalance.longTermReceivables?.string ?? "0",
    );
    longTermDeferredRevenue = new Decimal(
      fixedFeeConfig.reclassBalance.longTermDeferredRevenue?.string ?? "0",
    );
    unbilledAccountsReceivables = new Decimal(
      fixedFeeConfig.reclassBalance.unbilledAccountsReceivables?.string ?? "0",
    );
    unearnedRevenue = new Decimal(
      fixedFeeConfig.reclassBalance.unearnedRevenue?.string ?? "0",
    );

    adjustmentData.push({
      adjustmentDate: "INITIAL RECLASS",
      longTermReceivables,
      longTermDeferredRevenue,
      unbilledAccountsReceivables,
      unearnedRevenue,
    });
  }

  adjustmentData.push(
    ...Object.entries(fixedFeeConfig.adjustments)
      .sort(([date, _], [date2, __]) => date.localeCompare(date2))
      .map(([date, adjustment]) => {
        const executed = executedAdjustmentDates.find((e) => e.date === date);
        const data: any = {
          adjustmentDate: date,
          periodStartDate: adjustment.periodStartDate,
          periodEndDate: adjustment.periodEndDate,
          activeBenefitCount: adjustment.activeBenefitCount,
          deferredRevenue: adjustment.deferredRevenue,
          revisedPlanPrice: adjustment.revisedPlanPrice,
          adjustmentAmount: adjustment.adjustmentAmount,
          receivableShiftAmount: adjustment.receivableShiftAmount,
          deferralRevenueShiftAmount: adjustment.deferralRevenueShiftAmount,
          executionIndex: executed ? executed.index : 0,
        };

        // only populate reclass relevant items if reclass is enabled and this occurrence has executed
        if (fixedFeeConfig.reclassBalance && adjustment.adjustmentAmount) {
          longTermReceivables = longTermReceivables!.sub(
            new Decimal(adjustment.receivableShiftAmount?.string ?? "0"),
          );
          longTermDeferredRevenue = longTermDeferredRevenue!.sub(
            new Decimal(adjustment.deferralRevenueShiftAmount?.string ?? "0"),
          );
          unbilledAccountsReceivables = unbilledAccountsReceivables!.add(
            new Decimal(adjustment.receivableShiftAmount?.string ?? "0"),
          );
          unearnedRevenue = unearnedRevenue!.add(
            new Decimal(adjustment.deferralRevenueShiftAmount?.string ?? "0"),
          );

          data.longTermReceivables = longTermReceivables;
          data.longTermDeferredRevenue = longTermDeferredRevenue;
          data.unbilledAccountsReceivables = unbilledAccountsReceivables;
          data.unearnedRevenue = unearnedRevenue;
        }

        return data;
      }),
  );

  return (
    <Table
      columnDefinitions={adjustmentColumnDefinitions}
      items={adjustmentData}
      wrapLines={false}
      resizableColumns={false}
      header={
        <Header
          counter={`(${Object.entries(fixedFeeConfig.adjustments).length})`}
        >
          Adjustments
        </Header>
      }
    />
  );
};
